"use client"

import { useLanguage } from "../context/LanguageContext"
import "./TermsOfService.css"

const TermsOfService = () => {
  const { t, direction } = useLanguage()

  return (
    <div className="terms-container" dir={direction}>
      <div className="terms-hero">
        <div className="hero-content">
          <h1>{t("Terms of Service")}</h1>
          <p>{t("Last updated: January 1, 2024")}</p>
        </div>
      </div>

      <div className="terms-content">
        <div className="container">
          <div className="terms-section">
            <h2>{t("1. Acceptance of Terms")}</h2>
            <p>
              {t(
                "By accessing and using WATref Calculator ('the Service'), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.",
              )}
            </p>
          </div>

          <div className="terms-section">
            <h2>{t("2. Description of Service")}</h2>
            <p>
              {t(
                "WATref Calculator is a web-based platform that provides evapotranspiration calculations, crop management tools, and irrigation planning services for agricultural purposes. The Service includes:",
              )}
            </p>
            <ul>
              <li>{t("Reference evapotranspiration (WATref) calculations")}</li>
              <li>{t("Crop coefficient management")}</li>
              <li>{t("Weather data integration")}</li>
              <li>{t("Farm and project management tools")}</li>
              <li>{t("Historical data analysis and reporting")}</li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>{t("3. User Accounts")}</h2>
            <p>{t("To use certain features of the Service, you must register for an account. You agree to:")}</p>
            <ul>
              <li>{t("Provide accurate, current, and complete information during registration")}</li>
              <li>{t("Maintain and promptly update your account information")}</li>
              <li>{t("Maintain the security of your password and account")}</li>
              <li>{t("Accept responsibility for all activities under your account")}</li>
              <li>{t("Notify us immediately of any unauthorized use of your account")}</li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>{t("4. Acceptable Use")}</h2>
            <p>{t("You agree not to use the Service to:")}</p>
            <ul>
              <li>{t("Violate any applicable laws or regulations")}</li>
              <li>{t("Infringe upon the rights of others")}</li>
              <li>{t("Transmit any harmful, threatening, or offensive content")}</li>
              <li>{t("Attempt to gain unauthorized access to the Service")}</li>
              <li>{t("Interfere with or disrupt the Service or servers")}</li>
              <li>{t("Use the Service for any commercial purpose without authorization")}</li>
              <li>{t("Reverse engineer or attempt to extract source code")}</li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>{t("5. Data and Privacy")}</h2>
            <p>
              {t(
                "Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your information when you use our Service. By using the Service, you agree to the collection and use of information in accordance with our Privacy Policy.",
              )}
            </p>
          </div>

          <div className="terms-section">
            <h2>{t("6. Intellectual Property")}</h2>
            <p>
              {t(
                "The Service and its original content, features, and functionality are and will remain the exclusive property of WATref Calculator and its licensors. The Service is protected by copyright, trademark, and other laws.",
              )}
            </p>

            <h3>{t("User Content")}</h3>
            <p>
              {t(
                "You retain ownership of any content you submit to the Service. By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, and distribute your content in connection with the Service.",
              )}
            </p>
          </div>

          <div className="terms-section">
            <h2>{t("7. Disclaimers and Limitations")}</h2>
            <p>
              {t(
                "The Service is provided 'as is' and 'as available' without any warranties of any kind. We do not guarantee that:",
              )}
            </p>
            <ul>
              <li>{t("The Service will be uninterrupted or error-free")}</li>
              <li>{t("The results obtained from the Service will be accurate or reliable")}</li>
              <li>{t("Any errors in the Service will be corrected")}</li>
              <li>{t("The Service will meet your specific requirements")}</li>
            </ul>

            <h3>{t("Agricultural Decisions")}</h3>
            <p>
              {t(
                "The calculations and recommendations provided by the Service are for informational purposes only. You are solely responsible for making agricultural and irrigation decisions. We recommend consulting with agricultural professionals before making significant changes to your farming practices.",
              )}
            </p>
          </div>

          <div className="terms-section">
            <h2>{t("8. Limitation of Liability")}</h2>
            <p>
              {t(
                "In no event shall WATref Calculator, its directors, employees, partners, agents, suppliers, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the Service.",
              )}
            </p>
          </div>

          <div className="terms-section">
            <h2>{t("9. Subscription and Payment")}</h2>
            <p>{t("Some features of the Service may require a paid subscription. By subscribing, you agree to:")}</p>
            <ul>
              <li>{t("Pay all applicable fees as described in your subscription plan")}</li>
              <li>{t("Provide accurate billing information")}</li>
              <li>{t("Authorize automatic renewal unless cancelled")}</li>
              <li>{t("Accept that fees are non-refundable except as required by law")}</li>
            </ul>

            <h3>{t("Cancellation")}</h3>
            <p>
              {t(
                "You may cancel your subscription at any time. Cancellation will take effect at the end of your current billing period.",
              )}
            </p>
          </div>

          <div className="terms-section">
            <h2>{t("10. Termination")}</h2>
            <p>
              {t(
                "We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, including if you breach these Terms. Upon termination, your right to use the Service will cease immediately.",
              )}
            </p>
          </div>

          <div className="terms-section">
            <h2>{t("11. Changes to Terms")}</h2>
            <p>
              {t(
                "We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the new Terms on this page and updating the 'Last updated' date. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.",
              )}
            </p>
          </div>

          <div className="terms-section">
            <h2>{t("12. Governing Law")}</h2>
            <p>
              {t(
                "These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts in [Your Jurisdiction].",
              )}
            </p>
          </div>

          <div className="terms-section">
            <h2>{t("13. Contact Information")}</h2>
            <p>{t("If you have any questions about these Terms of Service, please contact us:")}</p>
            <ul>
              <li>{t("Email: legal@etrefcalculator.com")}</li>
              <li>{t("Phone: +1 (555) 123-4567")}</li>
              <li>{t("Address: 123 Agriculture Tech Blvd, Suite 456, Tech City, TC 12345")}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TermsOfService
